'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import Select, { components } from 'react-select';
import { Plus, Save, X, Clock, DollarSign, FlaskConical } from 'lucide-react';
import { Container, Row, Card } from "reactstrap";
import { Col } from "react-bootstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useLoader } from '../../_helper/LoaderContext';
import { masterDepartment, masterParameter, masterSample, masterTest, updateTest, viewTest } from './service/test-service';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

// Custom Checkbox Option Component
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '38px',
        borderRadius: '0.375rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '2px 8px',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected || state.isFocused ? '' : '',
        color: '#333',
        ':active': {
            backgroundColor: '',
        },
    }),
};

export default function EditTest() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { setLoading } = useLoader();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        control,
        trigger
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            testName: '',
            testCode: '',
            department: null,
            sampleType: null,
            testMethod: null,
            price: '',
            tatValue: '',
            tatUnit: 'hours',
            interpretation: ''
        }
    });

    const [departmentOption, setDepartment] = useState([]);
    const [sample, setSample] = useState([]);
    const [testMethods, setTestMethods] = useState([]);
    const [parameterOptions, setParameterOptions] = useState([]);
    const [parameterGroups, setParameterGroups] = useState([]);

    const [userData] = useState(() => {
        return JSON.parse(localStorage.getItem("currentLab") || "{}");
    });

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Fetch all masters and wait for their responses
                const [deptRes, sampleRes, methodRes, paramRes] = await Promise.all([
                    masterDepartment(),
                    masterSample(),
                    masterTest(),
                    masterParameter()
                ]);

                // Process and store master data
                const departments = deptRes.data.success ? deptRes.data.data.data : [];
                const samples = sampleRes.data.success ? sampleRes.data.data : [];
                const methods = methodRes.data.success ? methodRes.data.data : [];
                const parameters = paramRes.data.success ? paramRes.data.data : [];

                // Update state
                setDepartment(departments);
                setSample(samples);
                setTestMethods(methods);

                if (paramRes.data.success) {
                    const formatted = parameters.map(item => ({
                        value: item._id,
                        label: item.name,
                        fullData: item
                    }));
                    setParameterOptions(formatted);
                }

                // Now fetch test data with the actual master data we just received
                if (id) {
                    await fetchTestDataWithMasters(id, departments, samples, methods, parameters);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [id]);

    // New function that accepts master data directly
    const fetchTestDataWithMasters = async (testId, departments, samples, methods, parameters) => {
        try {
            const res = await viewTest(testId);
            if (res.data.success) {
                const test = res.data.data;

                console.log('Looking for department:', test.departmentId, 'in', departments);

                // Find the selected options using the master data arrays directly
                const deptOption = departments.find(d => d._id === test.departmentId);
                const sampleFromTest = Array.isArray(test.samples) && test.samples.length > 0
                    ? test.samples[0]
                    : null;
                const sampleOption = samples.find(s =>
                    s._id === test.sampleTypeId || s._id === sampleFromTest?.sampleId
                );
                const methodOption = methods.find(t => t._id === test.methodId);

                console.log('Found department:', deptOption);

                // Format parameter options for lookup
                const paramOptionsMap = parameters.reduce((map, item) => {
                    map[item._id] = {
                        value: item._id,
                        label: item.name,
                        fullData: item
                    };
                    return map;
                }, {});

                reset({
                    testName: test.name || '',
                    testCode: test.code || '',
                    department: deptOption ? {
                        value: deptOption._id,
                        label: deptOption.name
                    } : null,
                    sampleType: sampleOption ? {
                        value: sampleOption._id,
                        label: sampleOption.name
                    } : null,
                    testMethod: methodOption ? {
                        value: methodOption._id,
                        label: methodOption.name
                    } : null,
                    price: test.cost?.toString() || '',
                    tatValue: test.turnaroundTime?.value?.toString() || '',
                    tatUnit: test.turnaroundTime?.unit || 'hours',
                    interpretation: test.interpretation || ''
                });

                // Process parameter groups
                let groups = [];

                if (test.parameterGroups && test.parameterGroups.length > 0) {
                    groups = test.parameterGroups.map(group => {
                        const selectedParams = group.parameters.map(p => {
                            const paramId = p.parameterId?._id || p.parameterId || p._id;
                            const masterMatch = paramOptionsMap[paramId];

                            return {
                                value: paramId,
                                label: p.name,
                                fullData: {
                                    _id: paramId,
                                    name: p.name,
                                    code: p.code || masterMatch?.fullData?.code || '',
                                    unitValue: p.unitValue || masterMatch?.fullData?.unitValue || '',
                                    type: p.type || masterMatch?.fullData?.type || 'quantitative',
                                    referenceRanges: p.referenceRanges || []
                                }
                            };
                        });

                        return {
                            id: Date.now() + Math.random(),
                            groupName: group.groupName || '',
                            parameters: selectedParams,
                            errors: { parameters: '' }
                        };
                    });
                } else if (test.parameters && test.parameters.length > 0) {
                    const selectedParams = test.parameters.map(p => {
                        const paramId = p._id || p.parameterId;
                        const masterMatch = paramOptionsMap[paramId];
                        return {
                            value: paramId,
                            label: p.name,
                            fullData: {
                                ...masterMatch?.fullData,
                                referenceRanges: p.referenceRanges || []
                            }
                        };
                    });

                    groups = [{
                        id: Date.now(),
                        groupName: t('Default Group'),
                        parameters: selectedParams,
                        errors: { parameters: '' }
                    }];
                } else {
                    groups = [{
                        id: Date.now(),
                        groupName: '',
                        parameters: [],
                        errors: { parameters: '' }
                    }];
                }

                setParameterGroups(groups);
            }
        } catch (err) {
            toast.warning(t("Failed to load test data"));
            console.error(err);
        }
    };

    const addParameterGroup = () => {
        setParameterGroups([
            ...parameterGroups,
            { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
        ]);
    };

    const removeParameterGroup = (id) => {
        if (parameterGroups.length === 1) {
            toast.warn(t("At least one parameter group is required"));
            return;
        }
        setParameterGroups(parameterGroups.filter(g => g.id !== id));
    };

    const updateGroupName = (id, value) => {
        setParameterGroups(prev =>
            prev.map(g => g.id === id ? { ...g, groupName: value } : g)
        );
    };

    const updateGroupParameters = (id, selected) => {
        setParameterGroups(prev =>
            prev.map(g => g.id === id ? { ...g, parameters: selected || [] } : g)
        );
    };

    const validateAllGroups = () => {
        let valid = true;
        setParameterGroups(prev =>
            prev.map(g => {
                const paramError = g.parameters.length > 0 ? '' : t('Select at least one parameter');
                if (paramError) valid = false;
                return { ...g, errors: { parameters: paramError } };
            })
        );
        return valid;
    };

    const onSubmit = async (data) => {
        if (!validateAllGroups()) {
            toast.warning(t("Please fix all parameter group errors"));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                testId: id,
                name: data.testName,
                code: data.testCode,
                departmentId: data.department?.value || data.department,
                departmentName: data.department?.label || departmentOption.find(d => d._id === data.department)?.name || "",
                sampleTypeId: data.sampleType?.value || data.sampleType,
                sampleTypeName: data.sampleType?.label || sample.find(s => s._id === (data.sampleType?.value || data.sampleType))?.name || "",
                samples: (() => {
                    const sampleId = data.sampleType?.value || data.sampleType;
                    const selectedSample = sample.find(s => s._id === sampleId);
                    if (!selectedSample) return [];
                    const assignedEquipment = (selectedSample.requiredEquipment || [])
                        .map((eq) => (typeof eq === "string" ? eq : eq?._id))
                        .filter(Boolean);
                    return [{
                        sampleId: selectedSample._id,
                        name: selectedSample.name || "",
                        description: selectedSample.description || "",
                        assignedEquipment,
                    }];
                })(),
                methodId: data.testMethod?.value || data.testMethod,
                methodName: data.testMethod?.label || testMethods.find(t => t._id === data.testMethod)?.name || "",
                labId: userData.labId,
                labName: "Main Laboratory",
                cost: Number(data.price),
                turnaroundTime: {
                    value: Number(data.tatValue),
                    unit: data.tatUnit || "hours"
                },
                interpretation: data.interpretation || "",
                parameterGroups: parameterGroups.map(group => ({
                    groupName: group.groupName.trim(),
                    parameters: group.parameters.map(p => ({
                        parameterId: p.value,
                        name: p.label,
                        code: p.fullData.code || "",
                        unitValue: p.fullData.unitValue || "",
                        type: p.fullData.type || "quantitative",
                        referenceRanges: Array.isArray(p.fullData.referenceRanges)
                            ? p.fullData.referenceRanges
                            : []
                    }))
                }))
            };

            const res = await updateTest(payload);
            if (res.data.success) {
                toast.success(t("Test updated successfully!"));
                navigate(`${process.env.PUBLIC_URL}/list-test`);
            }
        } catch (error) {
            console.error(error);
            toast.warning(error.response?.data?.message || t("Failed to update test"));
        } finally {
            setLoading(false);
        }
    };

    const departmentSelectOptions = departmentOption.map(d => ({
        value: d._id,
        label: d.name
    }));

    const sampleSelectOptions = sample.map(s => ({
        value: s._id,
        label: s.name
    }));

    const methodSelectOptions = testMethods.map(t => ({
        value: t._id,
        label: t.name
    }));

    const getAvailableParametersForGroup = (currentGroupId) => {
        const selectedInOtherGroups = parameterGroups
            .filter(g => g.id !== currentGroupId)
            .flatMap(g => g.parameters.map(p => p.value));

        return parameterOptions.filter(option =>
            !selectedInOtherGroups.includes(option.value)
        );
    };

    return (
        <>
            <Breadcrumbs mainTitle={t("Edit Test")} parent={t("Home")} subParent={t("Test List")} title={t("Edit Test")} />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <Card>
                            <div className="card-body p-3 pt-3">
                                <form onSubmit={handleSubmit(onSubmit)} noValidate>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Test Name")} <span className="text-danger">*</span></label>
                                            <input
                                                {...register('testName', {
                                                    required: t('Test name is required'),
                                                    minLength: { value: 3, message: t('Minimum 3 characters') }
                                                })}
                                                type="text"
                                                className={`form-control rounded-3 ${errors.testName ? 'is-invalid' : ''}`}
                                                placeholder={t("e.g. Comprehensive Metabolic Panel")}
                                            />
                                            {errors.testName && <div className="invalid-feedback">{errors.testName.message}</div>}
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Test Code")} <span className="text-danger">*</span></label>
                                            <input
                                                {...register('testCode', { required: t('Test code is required') })}
                                                type="text"
                                                className={`form-control rounded-3 ${errors.testCode ? 'is-invalid' : ''}`}
                                                placeholder={t("e.g. CMP001")}
                                            />
                                            {errors.testCode && <div className="invalid-feedback">{errors.testCode.message}</div>}
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Department")} <span className="text-danger">*</span>
                                                <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="dept-tooltip"></i>
                                                <Tooltip id="dept-tooltip" place="top" content={t('Clinical department responsible for this test.')} />
                                            </label>
                                            <Controller
                                                name="department"
                                                control={control}
                                                rules={{ required: t('Department is required') }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={departmentSelectOptions}
                                                        value={field.value}
                                                        onChange={(option) => field.onChange(option)}
                                                        onBlur={field.onBlur}
                                                        placeholder={t("Search department...")}
                                                        isSearchable
                                                        isClearable
                                                        className={`react-select-container ${errors.department ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        styles={customStyles}
                                                    />
                                                )}
                                            />
                                            {errors.department && <div className="text-danger tw-text-[0.875em] mt-1">{errors.department.message}</div>}
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Sample Type")} <span className="text-danger">*</span>
                                                <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="sample-tooltip"></i>
                                                <Tooltip id="sample-tooltip" place="top" content={t('Type of biological sample required for testing.')} />
                                            </label>
                                            <Controller
                                                name="sampleType"
                                                control={control}
                                                rules={{ required: t('Sample type is required') }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={sampleSelectOptions}
                                                        value={field.value}
                                                        onChange={(option) => field.onChange(option)}
                                                        onBlur={field.onBlur}
                                                        placeholder={t("Search sample type...")}
                                                        isSearchable
                                                        isClearable
                                                        className={`react-select-container ${errors.sampleType ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        styles={customStyles}
                                                    />
                                                )}
                                            />
                                            {errors.sampleType && <div className="text-danger tw-text-[0.875em] mt-1">{errors.sampleType.message}</div>}
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Test Method")} <span className="text-danger">*</span>
                                                <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="test-tooltip"></i>
                                                <Tooltip id="test-tooltip" place="top" content={t('Laboratory technique used to perform this test.')} />
                                            </label>
                                            <Controller
                                                name="testMethod"
                                                control={control}
                                                rules={{ required: t('Test method is required') }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={methodSelectOptions}
                                                        value={field.value}
                                                        onChange={(option) => field.onChange(option)}
                                                        onBlur={field.onBlur}
                                                        placeholder={t("Search method...")}
                                                        isSearchable
                                                        isClearable
                                                        className={`react-select-container ${errors.testMethod ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        styles={customStyles}
                                                    />
                                                )}
                                            />
                                            {errors.testMethod && <div className="text-danger tw-text-[0.875em] mt-1">{errors.testMethod.message}</div>}
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1">{t("Price")} <span className="text-danger">*</span></label>
                                            <input
                                                {...register('price', {
                                                    required: t('Price is required'),
                                                    pattern: { value: /^\d+(\.\d{1,2})?$/, message: t('Valid amount e.g. 1800 or 1800.50') }
                                                })}
                                                type="text"
                                                className={`form-control rounded-3 ${errors.price ? 'is-invalid' : ''}`}
                                                placeholder={t("e.g. 1800.00")}
                                            />
                                            {errors.price && <div className="invalid-feedback">{errors.price.message}</div>}
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-2">
                                                <Clock size={16} className="text-warning flex-shrink-0" /> {t("Turnaround Time (TAT)")} <span className="text-danger">*</span>
                                            </label>
                                            <div className="d-flex">
                                                <input
                                                    {...register('tatValue', {
                                                        required: t('Enter value'),
                                                        pattern: { value: /^\d+$/, message: t('Only numbers') }
                                                    })}
                                                    type="text"
                                                    className={`form-control rounded-start ${errors.tatValue ? 'is-invalid' : ''}`}
                                                    placeholder="6"
                                                    style={{ borderTopRightRadius: '0px', borderBottomRightRadius: '0px' }}
                                                />
                                                <select
                                                    {...register('tatUnit')}
                                                    className="form-select tw-bg-gray-300 rounded-end"
                                                    style={{ width: '130px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                                                >
                                                    <option value="minutes">{t("Minutes")}</option>
                                                    <option value="hours">{t("Hours")}</option>
                                                    <option value="days">{t("Days")}</option>
                                                </select>
                                            </div>
                                            {errors.tatValue && <div className="invalid-feedback d-block">{errors.tatValue.message}</div>}
                                        </div>

                                        <div className="col-md-8">
                                            <label className="form-label fw-semibold mb-1">{t("Interpretation (Optional)")}</label>
                                            <textarea
                                                {...register('interpretation')}
                                                className="form-control"
                                                rows="1"
                                                placeholder={t("Add clinical interpretation...")}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-2">
                                            <FlaskConical size={18} className="text-primary flex-shrink-0" />
                                            <span>{t("Bundles")}</span>
                                        </label>

                                        {parameterGroups.map((group, index) => (
                                            <div key={group.id} className="rounded-3 p-3 mb-3 position-relative" style={{ backgroundColor: '#f8f9fa', border: "2px solid #1b255b" }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="fw-semibold text-muted tw-text-xs text-uppercase">
                                                        {t("Bundle")} #{index + 1}
                                                    </span>
                                                    {parameterGroups.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeParameterGroup(group.id)}
                                                            className="btn btn-sm btn-outline-danger py-0.5 px-2 tw-text-xs d-inline-flex align-items-center gap-1"
                                                        >
                                                            <X size={13} /> {t("Remove")}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold mb-1">{t("Bundle Name")}</label>
                                                        <input
                                                            type="text"
                                                            value={group.groupName}
                                                            onChange={(e) => updateGroupName(group.id, e.target.value)}
                                                            className="form-control"
                                                            placeholder={t("e.g. LIVER FUNCTION")}
                                                        />
                                                    </div>

                                                    <div className="col-md-8">
                                                        <label className="form-label fw-semibold mb-1">{t("Biomarkers")} <span className="text-danger">*</span></label>
                                                        <Select
                                                            isMulti
                                                            isSearchable
                                                            closeMenuOnSelect={false}
                                                            hideSelectedOptions={false}
                                                            options={getAvailableParametersForGroup(group.id)}
                                                            value={group.parameters}
                                                            onChange={(selected) => updateGroupParameters(group.id, selected)}
                                                            placeholder={t("Search and select parameters...")}
                                                            classNamePrefix="react-select"
                                                            components={{ Option }}
                                                            styles={customStyles}
                                                        />
                                                        {group.errors?.parameters && (
                                                            <small className="text-danger d-block mt-1">{group.errors.parameters}</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="d-flex justify-content-end mb-3">
                                            <button type="button" onClick={addParameterGroup} className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1">
                                                <Plus size={15} /> {t("Add Another Bundle")}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                        <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-1">
                                            <Save size={16} /> {t("Update Test")}
                                        </button>
                                        <Link to={`${process.env.PUBLIC_URL}/list-test`}>
                                            <button type="button" className="btn btn-secondary">{t("Cancel")}</button>
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}